import { useWeb3ModalAccount, useDisconnect } from '@web3modal/ethers/vue';
import type { IUserModel } from '~/types/apiService';
import { apiUrls, HEAD_OF_SALES } from '~/utils/constants';
import { useMainStore } from '~/stores/mainStore';
import { getBlockCheckCache, setBlockCheckCache } from '~/utils/blockCheckCache';

interface IBlockedCheckResponse {
  blocked: boolean;
}

export default defineNuxtPlugin({
  dependsOn: ['web3-modal'],
  hooks: {
    'vue:setup'() {
      const store = useMainStore();
      const { address, isConnected } = useWeb3ModalAccount();
      const { disconnect } = useDisconnect();
      const { apiUrl } = useEnvs();
      const route = useRoute();
      const refId = useCookie('refId', { default: () => HEAD_OF_SALES });

      // Set refId from query parameter if present, otherwise use default
      if (route.query.ref) {
        refId.value = route.query.ref.toString();
      } else {
        refId.value = HEAD_OF_SALES;
      }

      // Address block check with caching
      const checkBlockStatus = async (addr: string): Promise<IBlockedCheckResponse> => {
        // Check cache
        const cached = getBlockCheckCache(addr);
        if (cached) {
          return { blocked: cached.blocked };
        }

        // If no cache, make request
        const result = await $fetch<IBlockedCheckResponse>('https://red-cell-9b77.helmut-22d.workers.dev', {
          query: { address: addr }
        }).catch(() => ({ blocked: false })); // On API error - allow access

        // Save to cache
        setBlockCheckCache(addr, result.blocked);
        return result;
      };

      const blockStatus = ref<IBlockedCheckResponse | null>(null);

      // Load block status when address changes
      watch(
        address,
        async (newAddress) => {
          if (!newAddress) {
            blockStatus.value = null;
            return;
          }

          blockStatus.value = await checkBlockStatus(newAddress);
        },
        { immediate: true }
      );

      // Main user data request
      const { execute: fetchUserData } = useFetch<IUserModel>(apiUrls.v1.user, {
        baseURL: apiUrl,
        query: {
          address: address
        },
        immediate: false, // Don't run automatically
        onResponse: ({ response }) => {
          if (route.query.ref) {
            refId.value = route.query.ref.toString();
          } else {
            refId.value = HEAD_OF_SALES;
          }

          if (response.ok) {
            store.setUserData(response._data);
          }
        },
        key: 'userData'
      });

      // Watch for query.ref changes to update refId
      watch(
        () => route.query.ref,
        (newRef) => {
          if (newRef) {
            refId.value = newRef.toString();
          } else {
            refId.value = HEAD_OF_SALES;
          }
        }
      );

      // Watch for address and block status changes
      watch(
        [address, blockStatus, () => route.query.ref],
        async ([newAddress, blockData]) => {
          // If no address, clear data
          if (!newAddress) {
            store.setUserData(null);
            return;
          }

          // Wait for block check result
          if (blockData === null || blockData === undefined) {
            return;
          }

          // If address is blocked
          if (blockData.blocked) {
            console.warn('Address is blocked:', newAddress);

            // Disconnect wallet
            await disconnect();

            // Clear user data
            store.setUserData(null);

            // Show modal
            if (process.client) {
              store.userBlockInfoModalOpen = true;
            }

            return;
          }

          // If address is not blocked, load user data
          await fetchUserData();
        },
        { immediate: true }
      );

      // Additional protection: intercept connection attempts
      watch(isConnected, async (connected) => {
        if (connected && address.value) {
          // Check using cache
          const checkResult = await checkBlockStatus(address.value);

          if (checkResult.blocked) {
            await disconnect();
            store.setUserData(null);

            if (process.client) {
              store.userBlockInfoModalOpen = true;
            }
          }
        }
      });
    }
  }
});
